<template>
  <div>
    <NuxtLayout
      name="asuostaticlayout"
      :title="$page?.interestArea?.title"
      :sub-title="$page?.interestArea.subTitle"
      :image-source="$page?.interestArea.heroImage"
      :is-header-sticky="false"
      :program-list="$page?.RfiDegreeSeekingPrograms"
      :is-sticky-brand-nav="false"
      :is-study-page="true"
    >
      <NavbarStickyAtlas :nav-items="navItems" title="On this page:">
        <template #stuckMenu>
          <div
            class="d-flex justify-content-end flex-direction-row mb-space-xxxs"
          >
            <ButtonApplyNow
              type="modal"
              event-region="main content"
              event-section="secondary navbar"
            >
            </ButtonApplyNow>
            <button
              class="btn btn-secondary fs-medium fw-bold ms-space-sm"
              @click="smoothScrollTo('hero-rfi-asuo-static')"
            >
              Request info
            </button>
          </div>
        </template>
      </NavbarStickyAtlas>
      <!-- Overview -->
      <SectionApollo
        :title="$page?.interestArea.introTitle!"
        :image="$page?.interestArea.introImage!"
        class="bg-white"
        image-position="right"
        id="program-intro"
      >
        <div v-html="$page?.interestArea.introDescription!"></div>
      </SectionApollo>
      <!-- degree search section -->
      <section
        class="pt-lg-space-xl pb-space-lg pb-lg-space-xxl bg-white"
        id="degree-search-section"
        ref="rDegreeSearch"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-9">
              <h2 class="h2-medium mb-space-sm">
                {{ $page?.interestArea.degreeSearchTitle }}
              </h2>
              <div
                class="fs-medium pb-space-md d-none d-lg-block"
                v-html="$page?.interestArea.degreeSearchDescription"
              ></div>
            </div>
          </div>
          <div id="degree-search">
            <div class="row">
              <div class="col-12 col-lg-5">
                <input
                  role="search"
                  aria-label="Search"
                  v-model="queries"
                  type="text"
                  class="form-control mb-space-xs mb-lg-space-lg"
                  placeholder="Search"
                />
              </div>
              <div class="col-12 col-lg-3">
                <select
                  class="form-select mb-space-lg"
                  v-model="selectedCategory"
                  @change="handleDegreeTypeChange"
                  aria-label="Default select example"
                >
                  <option :value="null">Degree type</option>
                  <template v-for="category in $page?.allCategory">
                    <option :value="category.title">
                      {{ category.title }}
                    </option>
                  </template>
                </select>
              </div>
            </div>
            <!-- total -->
            <div class="row">
              <div class="col-12">
                <p class="fs-xs fw-bold mb-0 pb-space-sm">
                  {{ resultsFiltered?.length ?? 0 }} programs total
                </p>
              </div>
              <div class="col-12" v-if="resultsFiltered.length < 1">
                <p>
                  No results found based on the current criteria. Please visit
                  our
                  <NuxtLink
                    to="/online-degree-programs/"
                    class="rds-link"
                    @click.prevent="handleNoResultsLink"
                  >
                    Online Degree Programs
                  </NuxtLink>
                  page for a full list of programs.
                </p>
              </div>
            </div>
            <!-- degree search results -->
            <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
              <ClientOnly>
                <NuxtLink
                  :to="result.item.detailPage!"
                  class="col d-flex"
                  v-for="result in paginatedData"
                  data-cy="online-degree-programs-degree-search-card"
                  :key="result.item.title!"
                >
                  <CardDegreeSearch
                    class="flex-fill mb-space-xs mb-lg-space-md mx-0 text-dark-3"
                    border-variant="light-2"
                    :show-label="true"
                    :label="result?.item?.category?.title!"
                    :image-source="result.item.degreeImage!"
                    :image-alt="result.item.title!"
                    label-size="xs"
                    :title="result.item.title!"
                    title-size="medium"
                    :show-description="false"
                    :show-footer="true"
                    :show-cta="true"
                    footer-cta-size="medium"
                    footer-label-size="small"
                    @cardClick="handleCardClick(result.item, $event)"
                    @ctaClick="
                      handleCardLinkEvent(result.item, 'learn more', $event)
                    "
                    @icon-click="
                      handleCardLinkEvent(result.item, 'right chevron', $event)
                    "
                    footer-cta-text="Learn more"
                    :footer-label="formattedFooterLabel(result.item.nextStartDate!)"
                    :footer-cta-link="result.item.detailPage!"
                  ></CardDegreeSearch>
                </NuxtLink>
              </ClientOnly>
            </div>
            <div
              class="row"
              v-if="resultsFiltered.length > PAGE_DEFAULTS.PER_PAGE"
            >
              <RdsPagination
                aria-controls=""
                ariaLabel="Pagination"
                v-model="currentPage"
                align="center"
                pills
                hide-ellipses
                prev-class="pag-nav-small"
                next-class="pag-nav-small"
                last-class="pag-nav-small"
                first-class="pag-nav-small"
                class="mt-space-md degree-search-pagination px-gutter"
                :perPage="PAGE_DEFAULTS.PER_PAGE"
                first-text="First"
                last-text="Last"
                next-text="Next"
                prev-text="Prev"
                :limit="5"
                @page-click="handlePaginationChange"
                size="md"
                :totalRows="resultsFiltered.length"
              >
              </RdsPagination>
            </div>
          </div>
        </div>
      </section>
      <!-- Careers -->
      <section
        class="bg-light-2 py-space-md py-md-space-lg py-lg-space-xxl"
        id="study-area-careers"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 offset-lg-1 col-lg-11">
              <h2 class="h2-medium mb-space-sm mb-lg-space-md">
                {{ $page?.interestArea.skillsCareersTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <!-- cards -->
          <div class="row">
            <div class="col-12 col-lg-10 offset-lg-1">
              <CardInfoHorizontal
                v-for="(item) in $page?.interestArea.skillsCareersCards!"
                :key="item?.id"
                :bg-image-source="item?.image!"
                :display-logo="false"
                :display-label="false"
                :title="item?.title!"
                title-level="h3"
                title-size="large"
                :display-cta="false"
                border-variant="#fafafa"
                class="mb-space-xs mb-lg-space-md"
              >
                <div
                  class="mb-space-sm mb-lg-space-md"
                  v-html="item?.text"
                ></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>
      <!-- Accolades -->
      <SectionApollo
        :title="$page?.interestArea.accoladesTitle!"
        :image="$page?.interestArea.accoladesImage!"
        image-position="right"
        class="bg-white"
        id="study-area-accolades"
      >
        <div v-html="$page?.interestArea.accoladesDescription"></div>
      </SectionApollo>
      <!-- news section -->
      <section
        v-if="
          $page?.interestArea.relatedNewsArticle &&
          $page?.interestArea.relatedNewsArticle?.length > 0
        "
        class="py-space-xs pb-md-space-lg pt-md-space-xs pt-lg-space-md pb-lg-space-xxl bg-white"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <div class="col-12 px-0">
                <h2
                  class="h2-medium mb-space-sm mb-lg-space-md px-space-xs px-lg-0"
                >
                  {{ $page?.interestArea.relatedNewsTitle }}
                </h2>
                <div
                  class="bg-secondary title-underline mb-space-md mx-space-xs mx-lg-0"
                ></div>
              </div>
            </div>
          </div>
          <!-- cards -->
          <div class="row">
            <div
              class="col-12 col-md-6 col-lg-4 mb-space-xs mb-lg-space-sm"
              v-for="item in $page?.interestArea.relatedNewsArticle"
              :key="item?.id"
            >
              <CardImageArticle
                class="h-100"
                :clickable-card="true"
                @card-click="
                  handleNewsLinkClick(
                    item?.detailPage!,
                    item?.title!,
                    'read more',
                    $event
                  )
                "
                @cta-click="
                  handleNewsLinkClick(
                    item?.detailPage!,
                    item?.title!,
                    'read more',
                    $event
                  )
                "
                border-variant="light-4"
                :title="item?.title!"
                :image-source="item?.coverImage!"
                :image-alt="item?.coverImageAlt!"
                :date="item?.updatedDate!"
                :minutes="item?.timeToRead!"
                :cta-link="item?.detailPage!"
              ></CardImageArticle>
            </div>
          </div>
        </div>
      </section>
      <SectionParallaxApollo
        backdrop-height="374"
        bg-image-source="https://live-asuocms.ws.asu.edu/sites/default/files/AreasOfStudy.png"
        lg-columns="6"
        offset-lg-columns="3"
        title="Explore more degree programs"
        title-level="h2"
        :highlight-width="8"
        :cta-as-button="true"
        cta-button-variant="secondary"
        cta-text="Browse interest areas"
        cta-text-size="medium"
        cta-text-weight="bold"
        class="py-space-md pt-lg-space-xxl pb-lg-0 bg-white"
        cta-text-variant="dark-3"
        cta-link="/study/"
        cta-target="_self"
        @ctaClick="handleApolloCtaClick($event)"
      >
        <p class="mb-space-sm">
          ASU Online offers over 300 degrees and certificates in a plethora of
          fields. Check out other areas of interest to find the right program
          for you.
        </p>
      </SectionParallaxApollo>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardDegreeSearch } from "@rds-vue-ui/card-degree-search";
import { CardImageArticle } from "@rds-vue-ui/card-image-article";
import { CardInfoHorizontal } from "@rds-vue-ui/card-info-horizontal";
import { NavbarStickyAtlas } from "@rds-vue-ui/navbar-sticky-atlas";
import { SectionApollo } from "@rds-vue-ui/section-apollo";
import { SectionParallaxApollo } from "@rds-vue-ui/section-parallax-apollo";
import { watchDebounced } from "@vueuse/core";
import { UseFuseOptions, useFuse } from "@vueuse/integrations/useFuse";
import { FuseResult } from "fuse.js";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";

import {
  Category,
  DegreeSeekingProgram,
  InterestArea,
} from "~/server/resolvers/resolvers-types";
const schemaService = useSchemaService();
const route = useRoute();
const siteUrl = useRuntimeConfig().public.siteUrl;
interface IPage {
  allCategory: Category[];
  interestArea: InterestArea;
  allDegreeSeekingProgram: DegreeSeekingProgram[];
  RfiDegreeSeekingPrograms: DegreeSeekingProgram[];
}
/*
********************************************
Page quires
********************************************
*/
const $page = await usePageQuery<IPage>(
  `#graphql
      query($slug: String!, $filter: filterInput $sort: sortInput){
        allCategory {
          id
          title
        }
        interestArea(slug: $slug) {
          id
          metaTitle
          metaDescription
          categoryBySlug {
            slug
            detailPage
            title
            id
          }
          title
          subTitle
          heroImage
          introTitle
          introDescription
          introImage
          degreeSearchTitle
          degreeSearchDescription
          skillsCareersTitle
          skillsCareersCards {
            title
            text
            image
          }
          accoladesTitle
          accoladesDescription
          accoladesImage
          relatedNewsTitle
          relatedNewsArticle {
            title
            detailPage
            coverImage
            coverImageAlt
            updatedDate
            timeToRead
          }
        }
        allDegreeSeekingProgram (sort: $sort){
          id
          code
          title
          alternateTitle
          shortDescription
          detailPage
          degreeImage
          nextStartDate
          detailPage
          interestAreas {
            id
          }
          category {
            title
          }
        }
        RfiDegreeSeekingPrograms:allDegreeSeekingProgram(filter: $filter, sort: $sort){
          id
          code
          title
          shortDescription
          detailPage
          degreeImage
          nextStartDate
          detailPage
          category {
            title
          }
          interestAreas {
            id
          }
        }
      }
      `,
  {
    slug: route.params.slug as string,
    filter: {
      programFeature: {
        eq: "b4e8dd15-fed3-4594-a24b-ad8147dc9e26",
      },
    },
    sort: {
      by: "title",
      order: "ASC",
    },
  },
  { key: route.params.slug as string }
);

const taxonomy = $page?.interestArea.categoryBySlug?.id!;

// modify $page.allDegreeSeekingProgram by filtering the value based on interest area
const degreeSeekingProgram = $page?.allDegreeSeekingProgram.filter((program) =>
  (program.interestAreas ?? []).some(
    (area) => area?.id === (taxonomy as string)
  )
);
if ($page?.allDegreeSeekingProgram) {
  $page.allDegreeSeekingProgram = degreeSeekingProgram ?? [];
}

const RfiDegreeSeekingPrograms = $page?.RfiDegreeSeekingPrograms.filter(
  (program) =>
    (program.interestAreas ?? []).some(
      (area) => area?.id === (taxonomy as string)
    )
);
if ($page?.RfiDegreeSeekingPrograms) {
  $page.RfiDegreeSeekingPrograms = RfiDegreeSeekingPrograms ?? [];
}
/*
********************************************
Page meta tags
********************************************
*/
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});

useHead({
  title: $page?.interestArea.metaTitle,
  meta: [
    {
      name: "Title",
      content: $page?.interestArea.metaTitle,
    },
    {
      name: "Description",
      content: $page?.interestArea.metaDescription,
    },
    ...useUtilityService().generateSocialTags(
      $page?.interestArea.metaTitle as string,
      $page?.interestArea.metaDescription as string,
      $page?.interestArea.heroImage as string
    ),
  ],
  link: [
    {
      rel: "canonical",
      href: `${siteUrl}${$page?.interestArea.categoryBySlug?.detailPage!}`,
    },
  ],
  script: [
    schemaService.generateInterestAreaTemplateSchema(
      $page?.interestArea,
      $page?.allDegreeSeekingProgram
    ),
  ],
});
/*
********************************************
Page data
********************************************
*/

const navItems = {
  "program-intro": "Overview",
  "degree-search-section": "Degrees",
  "study-area-careers": "Careers",
  "study-area-accolades": "Accolades",
};

const formattedSection = computed<string | null>(() => {
  return $page?.interestArea.degreeSearchTitle!.toLowerCase() || null;
});

const formattedText = computed(() => {
  return selectedCategory.value?.toLowerCase() || null;
});

/*
********************************************
Methods
********************************************
*/
const smoothScrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    analyticsComposable.trackEvent({
      event: "link",
      name: "onclick",
      action: "click",
      type: "internal link",
      region: "navbar",
      section: "sticky navbar",
      text: "request info",
      component: "sticky navbar",
    });
  }
};
const handleNoResultsLink = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.interestArea.degreeSearchTitle!.toLowerCase() as string,
    "online degree programs",
    "degree search"
  );
};
const formattedFooterLabel = (nextStartDate: string) => {
  return `Next start date: ${nextStartDate}`;
};
/*
********************************************
Fuse search
********************************************
*/
const queries = ref<string | null>(null);
const selectedCategory = ref<string | null>(null);

const fuseOptions: UseFuseOptions<DegreeSeekingProgram> = {
  fuseOptions: {
    // set minMatchCharLength to 1 otherwise search bar will not filter programs with long title
    isCaseSensitive: false,
    includeScore: true,
    useExtendedSearch: true,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    location: 0,
    threshold: 0.6,
    distance: 200,
    keys: ["title", "category.title"],
  },
  matchAllWhenSearchEmpty: true,
};
const searchOptions = ref<DegreeSeekingProgram[]>(
  ($page?.allDegreeSeekingProgram ?? []) as DegreeSeekingProgram[]
);
// logic to send query input to data layer after slight delay
watchDebounced(
  queries,
  () => {
    analyticsComposable.trackEvent({
      event: "search",
      name: "onenter",
      action: "type",
      type: "degree search",
      region: "main content",
      section: formattedSection.value || "null",
      text: formattedText.value || "N/A",
      component: "degrees",
    });
  },
  { debounce: 1500 }
);
// logic to ensure page number is never greater than length of pages, or 0. watching search string and category
const keepPageInRange = () => {
  if (
    currentPage.value >
    Math.ceil(resultsFiltered.value.length / PAGE_DEFAULTS.PER_PAGE)
  ) {
    currentPage.value = Math.ceil(
      resultsFiltered.value.length / PAGE_DEFAULTS.PER_PAGE
    );
  } else if (currentPage.value === 0) {
    currentPage.value = 1;
  }
};
watch(queries, () => keepPageInRange(), { flush: "post" });
watch(selectedCategory, () => keepPageInRange(), { flush: "post" });

const resultsFiltered = ref<FuseResult<DegreeSeekingProgram>[]>([]);
const { fuse, results } = useFuse("", searchOptions, fuseOptions);
resultsFiltered.value = results.value;
const preparedSchema = computed(() => {
  const schema = [];
  if (selectedCategory.value) {
    schema.push({
      $or: [{ "category.title": `=${selectedCategory.value}` }],
    });
  }
  if (queries.value) {
    schema.push({
      $or: [{ title: `~${queries.value}` }],
    });
  }
  return schema;
});

enum PAGE_DEFAULTS {
  PER_PAGE = 12,
}
const rDegreeSearch = ref<HTMLElement | null>(null);
const currentPage = ref<number>(1);
const paginatedData = ref<FuseResult<DegreeSeekingProgram>[]>([]);
const fetchData = () => {
  const start = (currentPage.value - 1) * PAGE_DEFAULTS.PER_PAGE;
  const end = start + PAGE_DEFAULTS.PER_PAGE;
  paginatedData.value = resultsFiltered.value.slice(start, end);
};

const handlePaginationChange = (event: Event, page: number) => {
  const section = $page?.interestArea.degreeSearchTitle!;
  const label = (event.target as HTMLElement).innerText;
  const text = !isNaN(parseInt(label))
    ? `page ${page}`
    : label.toLocaleLowerCase();
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "pagination",
    "main content",
    section.toLowerCase(),
    text,
    "degrees"
  );
  fetchData();
  rDegreeSearch.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });
};
watchEffect(() => {
  if (preparedSchema.value.length > 0) {
    const results = fuse.value.search({
      $and: preparedSchema.value,
    });
    resultsFiltered.value = results;
    fetchData();
  } else {
    resultsFiltered.value = results.value;
    fetchData();
  }
});
const handleNewsLinkClick = (
  detailPage: string,
  title: string,
  component: string,
  e: Event
) => {
  e.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    title.toLowerCase(),
    formattedSection.value as string,
    component.toLowerCase()
  );
  navigateTo(detailPage, {
    open: {
      target: "_self",
    },
  });
};
const handleApolloCtaClick = (event: Event) => {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "explore more degree programs",
    "browse interest areas"
  );
  navigateTo("/study/", {
    open: {
      target: "_self",
    },
  });
};

const handleDegreeTypeChange = () => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "select degree type",
    "main content",
    formattedSection.value as string,
    formattedText.value || "select degree type",
    "degrees"
  );
};
const handleCardClick = (item: DegreeSeekingProgram, event: Event) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    item?.title?.toLowerCase() || "null",
    "learn more",
    "degree search"
  );
  navigateTo(item.detailPage, {
    open: {
      target: "_self",
    },
  });
};
const handleCardLinkEvent = (
  card: DegreeSeekingProgram,
  component: string,
  event: Event
) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    formattedSection.value || "null",
    `${card?.title?.toLowerCase()} ${component}`.toLocaleLowerCase(),
    "degrees"
  );
  navigateTo(card.detailPage, {
    open: {
      target: "_self",
    },
  });
};
</script>

<style lang="scss" scoped>
@media (max-width: 576px) {
  :deep(.pag-nav-small button) {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}

.title-underline {
  width: 64px;
  height: 5px;
}
.degree-search-pagination {
  --rds-pagination-color: var(--rds-dark-3);
  --rds-pagination-bg: transparent;
  --rds-pagination-border-radius: 50%;
  --rds-pagination-border-width: 0rem;
  --rds-pagination-border-color: transparent;
  --rds-pagination-hover-bg: var(--rds-primary);
  --rds-pagination-hover-color: var(--rds-white);
  --rds-pagination-hover-border-color: transparent;
  --rds-pagination-active-color: var(--rds-white);
  --rds-pagination-active-bg: var(--rds-primary);
  --rds-pagination-active-border-color: transparent;
  --rds-pagination-disabled-color: var(--rds-light-4);
  --rds-pagination-disabled-bg: transparent;
  --rds-pagination-disabled-border-color: transparent;
  --rds-pagination-focus-color: var(--rds-dark-3);
  --rds-pagination-focus-bg: transparent;
  --rds-pagination-padding-y: 0.5rem;
}
</style>
